import { useState } from 'react';
import { RemunerationProjection } from '@tosho/domain';
import type { FinancialOverview } from './financial-overview-api';
import { FinancialPilotageReal } from './FinancialPilotageReal';
import { RemunerationGoalDialog } from './RemunerationGoalDialog';
import { RevenueGoalDialog } from './RevenueGoalDialog';
import { TreasurySettingsDialog } from './TreasurySettingsDialog';

/** Démonstration isolée : mêmes cartes que le réel, aucun appel ni enregistrement réseau. */
export function FinancialPilotagePreview(): React.JSX.Element {
  const [period, setPeriod] = useState('2026-10');
  const [revenuePeriod, setRevenuePeriod] = useState('2026-10');
  const [treasuryEditing, setTreasuryEditing] = useState(false);
  const [treasuryThresholds, setTreasuryThresholds] = useState<readonly string[]>([
    '250000',
    '500000',
    '750000',
    '1000000',
  ]);
  const [salary, setSalary] = useState('250000');
  const [reserve, setReserve] = useState('830000');
  const [revenue, setRevenue] = useState('550000');
  const [point, setPoint] = useState('400000');
  const [pointDraft, setPointDraft] = useState('');
  const [revenueEditing, setRevenueEditing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const make = (month: string): FinancialOverview => {
    const months = Array.from({ length: 79 }, (_, index) => {
      const date = new Date(Date.UTC(2020, 8 + index, 1));
      const entry = date.toISOString().slice(0, 7);
      return {
        period: entry,
        takenCents: String(index % 5 === 0 ? 0 : 150000 + (index % 4) * 25000),
        ingoingsCents: '1025000',
        outgoingsCents: '600000',
        bankCents: String(450000 + (index % 9) * 85000),
        bankDate: `${entry}-28`,
        clientReceiptsCents: String(
          [460000, 370000, 500000, 350000, 370000, 850000, 700000][date.getUTCFullYear() - 2020] ??
            700000,
        ),
        expensesComplete: true,
        expenses: [
          {
            sourceConnectionId: '11111111-1111-4111-8111-111111111111',
            categoryId: 'software',
            name: 'Abonnements',
            paidCents: String(120000 + (index % 5) * 10000),
            htCents: String(100000 + (index % 5) * 8000),
          },
          {
            sourceConnectionId: '11111111-1111-4111-8111-111111111111',
            categoryId: 'social',
            name: 'Cotisations sociales & impôts',
            paidCents: String(180000 + (index % 3) * 20000),
            htCents: null,
          },
          {
            sourceConnectionId: '11111111-1111-4111-8111-111111111111',
            categoryId: 'vat',
            name: 'TVA',
            paidCents: '60000',
            htCents: null,
          },
        ],
      };
    }).filter((row) => row.period <= '2026-10');
    // Même projection annuelle que l'API, sur le jeu fictif complet, indépendamment
    // du mois consulté. Cela fournit aussi le flux net qui porte les couleurs.
    const projection = new RemunerationProjection(
      months.map((row) => ({
        period: row.period,
        takenCents: BigInt(row.takenCents),
        ingoingsCents: BigInt(row.ingoingsCents),
        outgoingsCents: BigInt(row.outgoingsCents),
        bankCents: BigInt(row.bankCents),
        bankDate: row.bankDate,
      })),
    );
    const years = Array.from({ length: 7 }, (_, index) => {
      const year = 2020 + index;
      const annual = projection.annual(year, '2026-10');
      return {
        ...annual,
        totalCents: annual.totalCents?.toString() ?? null,
        averageCents: annual.averageCents?.toString() ?? null,
        retainedCents: annual.retainedCents?.toString() ?? null,
      };
    });
    const row = months.find((entry) => entry.period === month);
    return {
      period: month,
      observedThroughPeriod: '2026-10',
      provisional: true,
      coverage: {
        salesDocuments: 6,
        purchaseDocuments: 5,
        salesBilledHtCents: '500000',
        purchasesBilledHtCents: '318000',
        salesKnownHtCents: '500000',
        salesValuedDocuments: 6,
        salesEligibleDocuments: 6,
        purchasesKnownHtCents: '318000',
        purchasesValuedDocuments: 5,
        purchasesEligibleDocuments: 5,
        directorRemunerationCents: row?.takenCents ?? null,
        operationIngoingsCents: '1025000',
        operationOutgoingsCents: '600000',
        operationValuedCount: 10,
        operationEligibleCount: 10,
        expenseCategories: [
          { name: 'Sous-traitance', cents: '142000' },
          { name: 'Logiciels', cents: '65000' },
          { name: 'Serveurs', cents: '44000' },
        ],
        operations: 10,
        movements: 10,
        declarations: 1,
      },
      declarations: [],
      urssafDeclaration: {
        declaredRevenueCents: '1025000',
        dueCents: '225500',
        confidence: 'confirme',
        sourceSnapshotId: null,
        importedAt: null,
        reason: 'Exemple fictif',
      },
      yearComparison: {
        previousPeriod: null,
        previousDeclaredRevenueCents: null,
        differenceCents: null,
        differencePercent: null,
        previousSourceSnapshotId: null,
        confidence: 'non_calculable',
      },
      bankBalance: {
        cents: row?.bankCents ?? '1010000',
        date: row?.bankDate ?? `${month}-28`,
        importedAt: null,
        confidence: 'incomplet',
        reason: 'Exemple fictif',
      },
      reserveTargetCents: reserve,
      salaryGoalCents: salary,
      monthlyRevenueGoalCents: revenue,
      breakEvenGoalCents: point,
      signalThresholds: [25, 50, 65, 80, 100],
      treasuryColorThresholdsCents: treasuryThresholds,
      remuneration: {
        provisionDueCents: '225500',
        provisionPeriod: month,
        months,
        years,
        advisedCents: salary,
        additionalCents: String(Math.max(0, Number(salary) - Number(row?.takenCents ?? 0))),
        retainedCents: '50000',
      },
      indicators: {
        receivedRevenueHt: { cents: '470000', confidence: 'confirme', reason: 'Exemple fictif' },
        availableIncome: { cents: salary, confidence: 'incomplet', reason: 'Exemple fictif' },
        personalWithdrawals: {
          cents: row?.takenCents ?? null,
          confidence: 'incomplet',
          reason: 'Exemple fictif',
        },
        breakEven: { cents: '400000', confidence: 'incomplet', reason: 'Exemple fictif' },
      },
    };
  };
  const overview = make(period);
  return (
    <>
      <FinancialPilotageReal
        overview={overview}
        demo
        onPeriod={setPeriod}
        revenueOverview={make(revenuePeriod)}
        onRevenuePeriod={setRevenuePeriod}
        onTreasurySetup={() => setTreasuryEditing(true)}
        onRevenueGoal={() => {
          setPointDraft(String(Number(point) / 100));
          setDraft(String(Number(revenue) / 100));
          setRevenueEditing(true);
        }}
        onGoal={() => {
          setDraft(String(Number(salary) / 100));
          setEditing(true);
        }}
      />
      {treasuryEditing ? (
        <TreasurySettingsDialog
          overview={overview}
          onCancel={() => setTreasuryEditing(false)}
          onSave={(thresholds, reserveCents) => {
            setTreasuryThresholds(thresholds);
            if (reserveCents !== undefined) setReserve(reserveCents);
            return Promise.resolve();
          }}
        />
      ) : null}
      {editing ? (
        <RemunerationGoalDialog
          overview={overview}
          draft={draft}
          onDraft={setDraft}
          onCancel={() => setEditing(false)}
          onSave={() => {
            const value = Number(draft);
            if (Number.isFinite(value) && value > 0 && value <= 10000000) {
              setSalary(String(Math.round(value * 100)));
              setEditing(false);
            }
          }}
        />
      ) : null}
      {revenueEditing ? (
        <RevenueGoalDialog
          pointDraft={pointDraft}
          onPointDraft={setPointDraft}
          draft={draft}
          onDraft={setDraft}
          onCancel={() => setRevenueEditing(false)}
          onSave={() => {
            const value = Number(draft);
            const pointValue = Number(pointDraft);
            if (
              Number.isFinite(value) &&
              value >= 1 &&
              value <= 10000000 &&
              Number.isFinite(pointValue) &&
              pointValue >= 1 &&
              pointValue <= 10000000
            ) {
              setRevenue(String(Math.round(value * 100)));
              setPoint(String(Math.round(pointValue * 100)));
              setRevenueEditing(false);
            }
          }}
        />
      ) : null}
    </>
  );
}
